import * as React from 'react'
import { DOM, E, flow, identity, IOO, pipe, RA, RR, s, t, T, TE, TO, v } from '@packages/prelude'
import { Autoplay, Navigation, SwiperOptions } from 'swiper'
import '../css/main.scss'
import * as domain from '@packages/domain'
import { react, swiper } from './commons'
import { ProductPreview } from './components'

const Swipers: RR.ReadonlyRecord<string, SwiperOptions> = {
  recommendationsSwiper: {
    direction: 'horizontal',
    modules: [Autoplay, Navigation],
    slidesPerView: 1,
    slidesPerGroup: 1,
    breakpoints: {
      768: {
        slidesPerView: 2,
        slidesPerGroup: 2
      }
    },
    loop: true,
    speed: 1000,
    autoplay: {
      delay: 5000
    },
    navigation: {
      nextEl: '.recommendation-button-next',
      prevEl: '.recommendation-button-prev'
    }
  }
}

pipe(Swipers, RR.foldMapWithIndex(s.Ord)(v.Monoid)(swiper.create))

const program = (config: domain.Api.Config.Type) =>
  pipe(
    TE.Do,
    TE.apS(
      'widgets',
      pipe(
        TE.of(document),
        TE.chainIOK(DOM.querySelectorAll('[data-widget-type="product-preview"]')),
        TE.chainOptionK(() => domain.Error.toDOMError('[data-widget-type="product-preview"]'))(identity),
        TE.chain(
          flow(
            RA.map(element =>
              pipe(
                (element as HTMLElement).dataset?.productId,
                TE.fromNullable(domain.Error.fromString('No product id found')),
                TE.chainEitherK(domain.Error.decodeWith(t.NumberFromString)),
                TE.chain(
                  flow(
                    TE.tryCatchK(
                      productId => fetch(`${config.wpApiBase}/indiaudio/product/${productId}`),
                      domain.Error.toUnknownError
                    ),
                    TE.filterOrElse(
                      response => response.ok,
                      () => domain.Error.fromString('fetch failed')
                    ),
                    TE.chain(TE.tryCatchK(response => response.json(), domain.Error.toUnknownError)),
                    TE.chainEitherK(domain.Error.decodeWith(domain.Product.Type)),
                    TE.map(product => RR.singleton(element.id, { ...product, element }))
                  )
                )
              )
            ),
            TE.sequenceArray
          )
        )
      )
    ),
    TE.apSW(
      'initialData',
      pipe(
        window.location.hash,
        TE.fromNullable(domain.Error.fromString('No hash found')),
        TE.chainEitherK(flow(s.unprepend('#') as any, domain.Error.decodeWith(t.NonEmptyString))),
        TE.chain(
          flow(
            TE.tryCatchK(hash => fetch(`${config.wpApiBase}/indiaudio/preview/${hash}`), domain.Error.toUnknownError),
            TE.filterOrElse(
              response => response.ok,
              () => domain.Error.toAudioNotFoundError(window.location.hash)
            ),
            TE.chain(TE.tryCatchK(response => response.json(), domain.Error.toUnknownError)),
            TE.chainEitherK(domain.Error.decodeWith(domain.Api.IALoadPreviewResponse.Type))
          )
        ),
        T.map(E.of) // wrap the eiter in a E.right to hand it over to the product preview component to handle the error there
      )
    ),
    TE.map(({ widgets, initialData }) =>
      pipe(
        widgets,
        RA.map(
          RR.map(({ element, ...props }) =>
            pipe(
              element,
              react.mountTo(() => (
                <ProductPreview.View
                  {...props}
                  key={element.id}
                  config={config}
                  title={(element as HTMLElement).dataset?.title}
                  initialData={initialData}
                />
              ))
            )
          )
        )
      )
    )
  )

pipe(
  window.wpConfig,
  domain.Error.decodeWith(domain.Api.Config.Type),
  TE.fromEither,
  TE.matchW(
    err => domain.Error.toMessage(err),
    config => program(config)().then(E.match(console.error, identity))
  )
)()

// react to click on 'add-to-cart' button to show notice
window.jQuery(document.body).on(
  'wc_fragment_refresh',
  pipe(
    TO.of({ document }),
    TO.apS(
      'formData',
      pipe(
        TO.of(new FormData()),
        TO.chainFirstIOK(fd => () => fd.append('action', 'indiaudio_add_to_cart_notice')),
        TO.chainFirstIOK(fd => () => fd.append('postNonce', window.wpConfig.postNonce))
      )
    ),
    TO.bind(
      'notice',
      TO.tryCatchK(({ formData }) =>
        fetch(window.wpConfig.ajaxUrl, {
          method: 'POST',
          body: formData
        }).then(response => response.json())
      )
    ),
    TO.chainFirstIOK(({ notice, document }) =>
      pipe(
        document,
        DOM.querySelector('.woocommerce'),
        IOO.chainFirstIOK(element => () => (element.innerHTML = notice?.data)),
        IOO.chainFirstIOK(element => () => element.scrollIntoView({ behavior: 'smooth' }))
      )
    )
  )
)
